import { AfterViewInit, Component, DestroyRef, ElementRef, inject, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import { DataTableDirective, DataTablesModule } from 'angular-datatables';
import { ADTSettings } from 'angular-datatables/src/models/settings';
import { loadPendingDonations } from 'app/manage-auctions/features/auction.actions';
import { selectPendingDonations } from 'app/manage-auctions/features/auction.selectors';
import { Subject } from 'rxjs';
import * as DialogActions from 'app/dialog/features/dialog.actions';
import { PendingService } from 'app/reports/services/pending.service';
import { CommonModule } from '@angular/common';
import { BulkCheckoutComponent } from 'app/event/components/bulk-checkout/bulk-checkout.component';
import { MdbModalService } from 'mdb-angular-ui-kit/modal';
import { ModalAuctionDetailService } from 'app/manage-auctions/services/modal-auction-detail/modal-auction-detail.service';

@Component({
  selector: 'app-auction-pending-tab',
  standalone: true,
  imports: [DataTablesModule,CommonModule],
  templateUrl: './auction-pending-tab.component.html',
  styleUrl: './auction-pending-tab.component.scss'
})

export class AuctionPendingTabComponent implements OnInit,AfterViewInit{
  destroyRef = inject(DestroyRef)

  @Input() public auction;

  public pendingDonations: any[];

  public saveCardRequired: boolean = false;
  public dtOptions = {};
  public dtTrigger: Subject<ADTSettings> = new Subject<ADTSettings>();

  public showBulkCheckout: boolean = false;
  
  @ViewChild('auctionPendingDatatable', { static: false }) datatableElement!: ElementRef;

  constructor(
    private store: Store,
    private pendingService: PendingService,
    private modalService: MdbModalService,
    private modalAuctionDetailService: ModalAuctionDetailService
  ) { }

  private pendingDonationsSelector$ = this.store.pipe(
    takeUntilDestroyed(),
    select(selectPendingDonations),
  )

  ngOnInit(): void {
    this.store.dispatch(
      loadPendingDonations({ campaignId: this.auction.id })
    )
  
    this.saveCardRequired = this.auction.campaignTypeData?.saveCardRequired || false;

    this.pendingDonationsSelector$.pipe(
    ).subscribe(list => {
      if (!list) {
        return;
      }
      
      this.pendingDonations = list;
      this.updateBulkCheckoutVisibility();
      this.renderDatatable();
    })

  }

  ngAfterViewInit(): void {
    if(this.pendingDonations?.length > 0){
      this.renderDatatable();
    }
  }

  groupOptionsByName(){
    return this.pendingDonations.reduce((acc, record) => {
      const name = record.donationInfo.presetInfo.name;
      const id = record.donationInfo.presetInfo.id;
      const key = `${name}|${id}`;
      acc.set(key, (acc.get(key) || 0) + 1);
      return acc;
    }, new Map<{name: string, id: string}, number>());
  }

  renderDatatable(){
    const self = this;
    const allDropdownColumns = [
      "Name", "Email","First Name","Last Name", "Phone", "Address",
      "Option", "Status", "Amount","Value", "Date","Info","Info Selected", "Action"
    ]

    const defaultVisibleColumns = [0,1,4,5,6,7,8,9,13]
    const locationPass = "report.campaigns.pending"
    window['datatableColumnsStateService'].initColumnsIfEmpty(locationPass,
      allDropdownColumns, defaultVisibleColumns);
  
    const currentColumns = window['datatableColumnsStateService'].getColumns(locationPass)
    if(!this.auction.additionalInfoDesc || this.auction.additionalInfoDesc === ''){
      const index = currentColumns.findIndex(x => x?.columnName === 'Info')
      if(index != -1){
          currentColumns[index] = null;
      }
    }

    if(!this.auction.additionalInfoSelectDesc || this.auction.additionalInfoSelectDesc === ''){
      const index = currentColumns.findIndex(x => x?.columnName === 'Info Selected')
      if(index != -1){
          currentColumns[index] = null;
      }
    }

    const visibleColumnIndexes = currentColumns
      .map((col, index) => (col && col.selected ? index : null))
      .filter(index => index !== null); 

    visibleColumnIndexes.push(13)

    const defaultOrder = currentColumns.findIndex(x => x.columnName === "Date")
    this.dtOptions ={
      data: this.pendingDonations,
      destroy: true,
      autoWidth: true,
      pageLength: 10,
      paging: true,
      order: [[ defaultOrder === - 1 ? 0 : defaultOrder, "desc" ]],
      deferRender: true,
      fixedColumns: {
        left:1,
      },
      columnDefs:[
        {
          targets: visibleColumnIndexes,
          visible: true,
        },
        {
          targets: '_all',
          visible: false,
        }
      ],
      dom: "<'table__header--paybee d-flex align-items-center justify-content-between pb-3 pt-3'<''B><'d-flex align-items-center'<'#auctionPendingDatatable-columns-filter.mx-3'><'filter--reminder mr-3'><''f>>><'table-responsive't><'table__footer--paybee d-flex align-items-center justify-content-between p-4'ip>",
      buttons: [{
          className: 'btn-copy-code btn btn-outline-grey btn-sm px-2 waves-effect',
          extend: 'excel',
          filename: 'Auction_PendingItems',
          text: "<i class='fas fa-cloud-download-alt mr-1'></i> Export",
          title: null,
          exportOptions: {
              stripHtml: true,
              columns: "thead th:not(.no-export)"
          }
      }],
      columns: [
          {
            data: "donationInfo.from.name",
            defaultContent: "NA",
            render(name, type, row) {
              if(type === 'display'){
                  const fundingSource = self.getFundingSource(row.donationInfo.from.id);
                  if (fundingSource) {
                    return `
                        <div class="d-flex align-items-center">
                            <span
                              data-toggle="fullNameTooltip"
                              data-placement="top"
                              data-original-title="${name}"
                              class="table-name-container blue-text mr-2 cursor--pointer w-auto"
                            >${name}</span>
                            <svg
                                focusable="false"
                                viewBox="0 0 32 21"
                                data-toggle="cardInfoTooltip"
                                style="width: 20px; cursor: pointer; flex-shrink:0"
                                placement="left"
                                title="${fundingSource.sourceName}"
                            >
                                <g fill="green" fill-rule="evenodd">
                                <g class="Icon-fill">
                                    <g transform="translate(0 2)">
                                    <path d="M26.58 19H2.42A2.4 2.4 0 0 1 0 16.62V2.38A2.4 2.4 0 0 1 2.42 0h24.16A2.4 2.4 0 0 1 29 2.38v14.25A2.4 2.4 0 0 1 26.58 19zM10 5.83c0-.46-.35-.83-.78-.83H3.78c-.43 0-.78.37-.78.83v3.34c0 .46.35.83.78.83h5.44c.43 0 .78-.37.78-.83V5.83z" opacity=".2"></path>
                                    <path d="M25 15h-3c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1zm-6 0h-3c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1zm-6 0h-3c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1zm-6 0H4c-.65 0-1-.3-1-1s.35-1 1-1h3c.65 0 1 .3 1 1s-.35 1-1 1z" opacity=".3"></path>
                                    </g>
                                </g>
                                </g>
                            </svg>
                        </div>
                    `;
                }
  
                return `<span 
                    data-toggle="fullNameTooltip"
                    data-placement="top"
                    data-original-title="${name}"
                    class="table-name-container blue-text cursor--pointer">${name}</span>`
              } 
  
              return name;
            },
            createdCell: function(cell, cellData, rowData, row, col){
              const callback = () => {  
                self.openAuctionDetailModal(rowData.donationInfo.from.id);
              }
              cell?.removeEventListener('click', callback);
              cell?.addEventListener('click', callback);
            }
          },
        {
          data: "donationInfo.from.email",
          defaultContent: "NA",
        },
        {
          data: "donationInfo.from.firstName",
        },
        {
          data: "donationInfo.from.lastName",
        },
        {
          data: "donationInfo.from.phone",
          defaultContent: "NA",
          render : function (phone) {
              if(phone != undefined && phone != null && phone != "") {
                  return phone
              } else {return "NA"}
          }
        },
        {
          data: "donationInfo.from.address",
          defaultContent: "NA",
          render : function (address) {
              if(address != undefined && address != null && address.street1 != "") {
                  return window['utils'].getStreetAddressText(address, true)
              } else {return "NA"}
          }
        },
        {
          data: "donationInfo",
          defaultContent: "Other",
          render: function(donationInfo) {
              var optionName = ""
              if(donationInfo.presetInfo){
                  optionName = donationInfo.presetInfo.name
              } else if(window['utils'].isValidNonEmptyArray(donationInfo.multiTickets)){
                  donationInfo.multiTickets.forEach(function(ticket){
                      optionName += ticket.quantity + " x " + ticket.optionInfo.name + "<br>";
                  });
              } else {
                  optionName = "Other"
              }

              return optionName
          }
        },
        {
          data: "id",
          render: function(data, type, row, meta ) {
              return "pending"
          }
        },
        {
          data: "donationInfo.amount",
          type: "num-fmt",
          render: function(amount, type, row ) {
              return window['utils'].getAmountDisplay(amount)
          }
        },
        {
          data: "donationInfo.campaignPreset.valueProvided",
          type: "num-fmt",
          render: function(data, type, row ) {
            return window['utils'].getAmountDisplay(data)
          }
      },
        {
          data: "created",
          type: "date",
          render: function(created, type, row ) {
              return  window['orgMain'].dateStringFromJSDate(new Date(created))
          }
        },
        {
          data: "donationInfo.campaignPreset.additionalInfoDesc",
          render: function(addInfo, type, row ) {
              if(addInfo != "") {
                  return addInfo.replace(/<[^>]*>/g, '')
              } 

              return "NA"
          },
          createdCell: function(cell, cellData, rowData, row, col){
            window['$']('[data-toggle="popover-hover-table"]').tooltip()
          }
        },
        {
          data: "donationInfo.campaignPreset.additionalInfoSelectDesc",
          render: function(selectInfo){
              if(selectInfo && selectInfo != ''){
                  return selectInfo
              }

              return "NA"
          }
        },
        {
          data: "donationInfo.from.id",
          orderable:false,
          createdCell: function(cell, cellData, rowData, row, col){
              const btn = cell.firstChild;
              const reloadCallback = () => {
                self.store.dispatch(
                  loadPendingDonations({ campaignId: self.auction.id })
                )
              }
              
              const clickCallback = (event) => {
                const page = 'auction-pending';
                const fundingSource = self.getFundingSource(cellData) ? [self.getFundingSource(cellData)] : [];
                window['orgMain'].sendCartReminder(event.currentTarget, page, reloadCallback, self.auction, fundingSource);
              }

              btn?.removeEventListener('click', clickCallback);
              btn?.addEventListener('click', clickCallback);
          },
          render: function(userId, type,row) {
              var campId = row.donationInfo.campaignData.id;
              return `<a class='blue-text' style='white-space: nowrap' id='remind_${userId}_${campId}'>Show Cart</a>`;
          },
        },
      ],
      initComplete: () => {
        let columnsDropdownHTML =  window['orgMain'].getColumnsDropdownHTML(currentColumns, self.dtOptions)
        $("#auctionPendingDatatable-columns-filter").html(columnsDropdownHTML);
        window['orgMain'].loadColumnsDropdown('auctionPendingDatatable-columns-filter', 'auctionPendingDatatable', locationPass);
      },
      drawCallback: function(){
        window['$']('[data-toggle="cardInfoTooltip"]').tooltip()
        
        const datatableEl = self.datatableElement.nativeElement as HTMLElement;
        const scrollContainer = datatableEl.parentElement;
    
        if (scrollContainer) {
          window['dataTableService'].registerScrollEvent(window['$'](scrollContainer));
        }

        setTimeout(() => {
          window['dataTableService'].scrollCallback(window['$'](scrollContainer))

          window['$']('[data-toggle="fullNameTooltip"]')
           .filter(function () {
              const isSelf = this.classList.contains('table-name-container');
              const container = isSelf ? this : this.querySelector('.table-name-container');
              return container && container.scrollWidth > container.offsetWidth;
            })
            .tooltip();
        })
      },
    }
    
    setTimeout(() => {
      // race condition fails unit tests if dtOptions isn't sent with dtTrigger
      if (this.dtTrigger.closed) {
        this.dtTrigger = new Subject<ADTSettings>();
      }

      this.dtTrigger.next(this.dtOptions);
    }, 200);
  }

  onClickAddPending(){
    const action = DialogActions.openDialog({
          payload: {
            name: "AddPending",
            options: {
              size: 'small',
              modalClass:'modal--paybee modal-lg',
              containerClass:'mdb-modal-backdrop-paybee'
            },
            initialState: {
              campaignTypes: [5],
              selectedCampaign: this.auction,
              callback: (data) => {
                this.pendingService.addToCart(data).pipe(
                  takeUntilDestroyed(this.destroyRef)
                ).subscribe(() => {
                  this.store.dispatch(
                    loadPendingDonations({ campaignId: this.auction.id })
                  )
                });
              }
            }
          }
        });

        this.store.dispatch(action);
  }

  openBulkCheckoutModal() {
    const modalRef = this.modalService.open(BulkCheckoutComponent, {
      data: {
        data: {
          placeOfCall: 'auction',
          campaignId: this.auction.id
        },
      },
      containerClass: 'mdb-modal-backdrop-paybee',
      modalClass: 'modal--paybee modal-lg',
      ignoreBackdropClick: true
    });

    modalRef.onClose.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((refreshDonations: boolean) => {
      if (refreshDonations) {
        this.store.dispatch(
          loadPendingDonations({ campaignId: this.auction.id })
        );
      }
    });
  }

  openAuctionDetailModal(userId) {
    const data = {
      userId,
      placeOfCall: 'bidderDetails'
    }

    this.modalAuctionDetailService.open(data);
  }

  private getFundingSource(userId: string) {
    if (!userId) {
      return null;
    }

    const foundDonation = this.pendingDonations.find(donation => donation.donationInfo.from.id === userId);
    if (!foundDonation) {
      return null;
    }

    const fundingSource = foundDonation?.fundingSource || null;

    return fundingSource;
  }

  private updateBulkCheckoutVisibility() {
    if (!this.saveCardRequired || !this.pendingDonations) {
      this.showBulkCheckout = false;
      return;
    }

    const hasFundingSource = this.pendingDonations.some(donation => 
      this.getFundingSource(donation.donationInfo.from.id)
    );

    this.showBulkCheckout = hasFundingSource;
  }
}
